import React, { useEffect, useState } from "react";
import { Info } from "lucide-react";

import { Callout } from "@/components/ui/Callout";
import Skeleton from "@/components/shared/Skeleton";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { plural } from "@/utils/formatText";

import CodeBlock from "./CodeBlock";
import ErrorNote from "./ErrorNote";
import OperationList from "./OperationList";

/**
 * The real SQL a revision runs, in one direction.
 *
 * This is what the screen shows instead of a written description. A migration's
 * docstring is typed by hand and most files here have none; the SQL is generated
 * from the same code that runs in production, so it is always there and always
 * exact. For the migration that split `jobs.experience` into a range, the
 * downgrade prints
 *
 *     UPDATE jobs SET experience = experience_min;
 *     ALTER TABLE jobs DROP COLUMN experience_max;
 *
 * which is the fact that a paragraph of prose used to have to explain.
 *
 * Two revisions cannot be rendered and both are normal rather than broken: a
 * merge revision has no SQL of its own, and a migration that reads the database
 * before deciding what to do cannot be rendered without running it. In both cases
 * the parsed operation list is shown instead, so there is always an answer.
 */
const RevisionSqlBlock = ({ revision, direction = "upgrade", fallbackOperations }) => {
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!revision) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await PlatformService.fetchRevisionSql(revision, direction);
                if (!cancelled) setResult(data);
            } catch (err) {
                if (!cancelled) setError(apiErrorMessageFormat(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [revision, direction]);

    if (loading) return <Skeleton className="h-20 w-full" />;
    if (error) return <ErrorNote>{error}</ErrorNote>;
    if (!result) return null;

    // Rendered, and there is something to show.
    if (result.available && result.statementCount > 0) {
        return (
            <div>
                <div className="text-[11px] text-text-secondary mb-1">
                    {plural(result.statementCount, "statement", "statements")}
                    {direction === "downgrade" ? " a downgrade runs" : " this runs"}
                </div>
                <CodeBlock>
                    {result.statements.map((statement, index) => (
                        <pre key={index} className="whitespace-pre-wrap mb-2">
                            {statement}
                        </pre>
                    ))}
                </CodeBlock>
            </div>
        );
    }

    // Rendered, and it does nothing. True of a merge, and of a revision whose
    // work is entirely in the version stamp.
    if (result.available) {
        return (
            <div className="text-[11px] text-text-secondary">
                This revision runs no statements in that direction.
            </div>
        );
    }

    // Could not be rendered. Say why, then show what is known from the code.
    return (
        <div className="space-y-2">
            <Callout tone="neutral" icon={Info} size="xs">
                <span className="font-medium block mb-0.5">
                    The SQL for this one cannot be shown
                </span>
                {result.reason}
            </Callout>
            {fallbackOperations?.length > 0 && (
                <div>
                    <div className="text-[11px] text-text-secondary mb-1">
                        Read from the migration's code instead:
                    </div>
                    <OperationList operations={fallbackOperations} />
                </div>
            )}
        </div>
    );
};

export default RevisionSqlBlock;
